import { supabase } from '../utils/supabase';
import {
  checkGameCorrectionEligibility,
  buildCorrectedEloPayload,
  buildSessionChainReplay,
} from '../utils/scoreCorrection';
import {
  correctSessionGameScoreCmd,
  correctSessionGameScorePostSessionCmd,
} from './sessionCommands';

/**
 * Load a single game with participants and elo_history for correction UI.
 */
export async function fetchGameCorrectionContext(gameId) {
  if (!gameId) return null;

  const { data, error } = await supabase
    .from('session_games')
    .select(`
      id,
      session_id,
      team_1_score,
      team_2_score,
      winning_team,
      created_at,
      game_type_id,
      players_session_games (
        player_id,
        team_number,
        result,
        pair_type,
        players (
          id,
          first_name,
          last_name,
          gender
        )
      ),
      elo_history (
        player_id,
        old_value,
        new_value,
        elo_change,
        pair_type
      )
    `)
    .eq('id', gameId)
    .single();

  if (error) {
    console.error('[scoreCorrection] fetchGameCorrectionContext failed:', error);
    throw new Error(error.message || 'Failed to load game');
  }

  return data;
}

export async function fetchPostSessionCorrectionGates(sessionId) {
  if (!sessionId) {
    return {
      ok: false,
      isLatestClubSession: false,
      withinCorrectionWindow: false,
      completedAt: null,
    };
  }

  const { data, error } = await supabase.rpc('get_post_session_score_correction_gates', {
    p_session_id: sessionId,
  });

  if (error) {
    console.error('[scoreCorrection] fetchPostSessionCorrectionGates failed:', error);
    return {
      ok: false,
      isLatestClubSession: false,
      withinCorrectionWindow: false,
      completedAt: null,
    };
  }

  return {
    ok: data?.ok === true,
    isLatestClubSession: Boolean(data?.is_latest_club_session),
    withinCorrectionWindow: Boolean(data?.within_correction_window),
    completedAt: data?.completed_at || null,
    sessionStatus: data?.session_status || null,
    windowHours: data?.window_hours ?? 72,
  };
}

export function triggerDerivedDataRebuildAfterScoreCorrection(sessionId) {
  if (!sessionId) return;
  supabase
    .rpc('rebuild_session_derived_data_after_score_correction', { p_session_id: sessionId })
    .then(({ data, error }) => {
      if (error) {
        console.warn('[scoreCorrection] Derived-data rebuild failed to start:', error);
        return;
      }
      if (data?.ok === false) {
        console.warn('[scoreCorrection] Derived-data rebuild returned:', data.code || data.message);
      }
    })
    .catch((err) => {
      console.warn('[scoreCorrection] Derived-data rebuild invoke error:', err);
    });
}

export { checkGameCorrectionEligibility, buildCorrectedEloPayload };

const STALE_CHAIN_MESSAGE =
  'A game finished while you were editing. Refresh and try again.';

function withStaleChainMessage(result) {
  if (result?.ok === false && result.code === 'STALE_CHAIN') {
    return { ...result, message: STALE_CHAIN_MESSAGE };
  }
  return result;
}

/**
 * Recompute ELO from stored prematch snapshots and persist the corrected score.
 */
export async function submitCompetitiveScoreCorrection({
  game,
  team1Score,
  team2Score,
  playersById = {},
  allSessionGames = [],
  correctionMode = 'live',
}) {
  if (correctionMode === 'post_session' || correctionMode === 'live_chain') {
    const replay = buildSessionChainReplay(
      game,
      { team1Score, team2Score },
      allSessionGames,
      playersById
    );
    const result = withStaleChainMessage(
      await correctSessionGameScorePostSessionCmd({
        sessionGameId: game.id,
        team1Score,
        team2Score,
        eloUpdates: replay.eloUpdates,
        chainUpdates: replay.chainUpdates,
      })
    );
    if (result.ok && correctionMode === 'post_session') {
      triggerDerivedDataRebuildAfterScoreCorrection(game.session_id);
    }
    return result;
  }

  const { updates } = buildCorrectedEloPayload(
    game,
    { team1Score, team2Score },
    playersById
  );

  return correctSessionGameScoreCmd({
    sessionGameId: game.id,
    team1Score,
    team2Score,
    eloUpdates: updates,
  });
}
